import {
  Localized,
  WithLocalizationProps,
  withLocalization,
} from '@fluent/react';
import * as React from 'react';
import { useState } from 'react';
import BalanceText from 'react-balance-text';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';

const NavigationPrompt = require('react-router-navigation-prompt').default;
import { Locale } from '../../../../stores/locale';
import { Notifications } from '../../../../stores/notifications';
import { Sentences } from '../../../../stores/sentences';
import { Sentence as SentenceType } from 'common';
import StateTree from '../../../../stores/tree';
import { Uploads } from '../../../../stores/uploads';
import { User } from '../../../../stores/user';
import API from '../../../../services/api';
import { trackRecording, getTrackClass } from '../../../../services/tracker';
import URLS from '../../../../urls';
import { localeConnector, LocalePropsFromState } from '../../../locale-helpers';
import Modal, { ModalButtons } from '../../../modal/modal';
import TermsModal from '../../../terms-modal';
import {
  CheckIcon,
  MicIcon,
  StopIcon,
  ArrowRight,
  FirefoxColor,
  ChromeColor,
  SafariColor,
  ReturnKeyIcon,
} from '../../../ui/icons';
import { Button, TextButton, LinkButton, Spinner } from '../../../ui/ui';
import { isIOS, isMobileSafari } from '../../../../utility';
import ContributionPage, {
  ContributionPillProps,
  SET_COUNT,
} from '../contribution';
import {
  RecordButton,
  RecordingStatus,
} from '../../../primary-buttons/primary-buttons';
import AudioWeb, { AudioError, AudioInfo } from './audio-web';
import RecordingPill from './recording-pill';
import { SentenceRecording } from './sentence-recording';
import SelectDevice from '../select-device';

import './speak.css';
import { ENVIRONMENT } from '../../../../constants';

const MIN_RECORDING_MS = 1000;
const MIN_RECORDING_MS_BENCHMARK = 500;
const MAX_RECORDING_MS = 10000;
const MIN_VOLUME = 8; // Range: [0, 255].

enum RecordingError {
  TOO_SHORT = 'TOO_SHORT',
  TOO_LONG = 'TOO_LONG',
  TOO_QUIET = 'TOO_QUIET',
}

const UnsupportedInfo = () => (
  <div className="empty-container">
    <div className="error-card card-dimensions unsupported">
      {isIOS() && !isMobileSafari() ? (
        <>
          <BalanceText>
            <Localized id="record-platform-not-supported-ios-non-safari" />
          </BalanceText>
          <SafariColor />
        </>
      ) : (
        <>
          <BalanceText>
            <Localized id="record-platform-not-supported" />
          </BalanceText>
          <p className="desktop">
            <Localized id="record-platform-not-supported-desktop">
              <BalanceText />
            </Localized>
          </p>
          <div>
            <a
              rel="noopener noreferrer"
              target="_blank"
              href="https://www.firefox.com/"
              title="Firefox">
              <FirefoxColor />
            </a>{' '}
            <a
              rel="noopener noreferrer"
              target="_blank"
              href="https://www.google.com/chrome"
              title="Chrome">
              <ChromeColor />
            </a>
          </div>
        </>
      )}
    </div>
  </div>
);

const NoSentencesAvailable = ({
  isLoading,
}: {
  isLoading: boolean;
}): JSX.Element => (
  <div className="empty-container">
    {isLoading ? (
      <>
        <Spinner></Spinner>
      </>
    ) : (
      <div className="error-card card-dimensions no-sentences-available">
        <Localized id="speak-empty-state">
          <span />
        </Localized>
        <LinkButton
          rounded
          blank
          href="https://common-voice.github.io/sentence-collector/">
          <ArrowRight className="speak-sc-icon" />{' '}
          <Localized id="speak-empty-state-cta">
            <span />
          </Localized>
        </LinkButton>
      </div>
    )}
  </div>
);

interface PropsFromState {
  api: API;
  locale: Locale.State;
  sentences: SentenceType[];
  user: User.State;
  isLoading: boolean;
}

interface PropsFromDispatch {
  addUploads: typeof Uploads.actions.add;
  addAchievement: typeof Notifications.actions.addAchievement;
  addNotification: typeof Notifications.actions.addPill;
  removeSentences: typeof Sentences.actions.remove;
  tallyRecording: typeof User.actions.tallyRecording;
  refreshUser: typeof User.actions.refresh;
  updateUser: typeof User.actions.update;
}

interface Props
  extends LocalePropsFromState,
    WithLocalizationProps,
    PropsFromState,
    PropsFromDispatch,
    RouteComponentProps<any, any, any> {}

interface State {
  clips: SentenceRecording[];
  recordedClips?: SentenceRecording[];
  isSubmitted: boolean;
  error?: RecordingError | AudioError;
  recordingStatus: RecordingStatus;
  rerecordIndex?: number;
  // showPrivacyModal: boolean;
  // showDiscardModal: boolean;
  language: string;
  recordProgress?: number;
  showSelectDevice: boolean;
  useExternalMic: boolean;
  devices: string[];
  audioNames: {
    dev_mac: string;
    file: string;
  }[];
  loading: boolean;
  recordingCountdown?: number;
  showBackdrop?: boolean;
}

const initialState: State = {
  clips: [], // TODO fill data here
  recordedClips: [],
  isSubmitted: false,
  error: null,
  recordingStatus: null,
  rerecordIndex: null,
  // showPrivacyModal: false,
  // showDiscardModal: false,
  language: '',
  recordProgress: 0,
  showSelectDevice: false,
  useExternalMic: false,
  devices: null,
  audioNames: null,
  loading: true,
  recordingCountdown: null,
};

class SpeakPage extends React.Component<Props, State> {
  state: State = initialState;
  demoMode = this.props.location.pathname.includes(URLS.DEMO);

  audio: AudioWeb;
  isUnsupportedPlatform = false;
  maxVolume = -1;
  recordingStartTime = 0;
  recordingStopTime = 0;
  audioLenght = -1;
  timer: NodeJS.Timer;
  stopScheudle: NodeJS.Timeout;
  showWave = true;

  async componentDidMount() {
    this.audio = new AudioWeb();
    this.audio.setVolumeCallback(this.updateVolume.bind(this));

    document.addEventListener('visibilitychange', this.releaseMicrophone);
    document.addEventListener('keyup', this.handleKeyUp);

    await this.fetchDevices();

    if (
      !this.audio.isMicrophoneSupported() ||
      !this.audio.isAudioRecordingSupported()
    ) {
      this.isUnsupportedPlatform = true;
    }

    const clips = await this.getLocaleClips(
      this.props.location.state.recordingLang
    );

    this.setState({
      clips: this.getExtendedClips(clips),
      loading: false,
    });
  }

  async getLocaleClips(lang: string): Promise<SentenceRecording[]> {
    const sentences = await this.props.api.fetchLangSentences(lang);
    const ret: SentenceRecording[] = sentences.map(s => ({
      sentence: s,
      recording: undefined,
    }));
    return ret;
  }

  async componentWillUnmount() {
    document.removeEventListener('keyup', this.handleKeyUp);

    document.removeEventListener('visibilitychange', this.releaseMicrophone);
    if (!this.isRecording) return;
    await this.audio.stop();
  }

  private get isRecording() {
    return this.state.recordingStatus === 'recording';
  }

  /**
   * Shortcuts which need more complex matching than a single "key comparison"
   * are handled here.
   * If possible use the `shortcuts` prop of `ContributionPage` instead.
   */
  private handleKeyUp = async (event: KeyboardEvent) => {
    let reRecordIndex = null;
    //for both sets of number keys on a keyboard with shift key
    if (event.code === 'Digit1' || event.code === 'Numpad1') {
      reRecordIndex = 0;
    } else if (event.code === 'Digit2' || event.code === 'Numpad2') {
      reRecordIndex = 1;
    } else if (event.code === 'Digit3' || event.code === 'Numpad3') {
      reRecordIndex = 2;
    } else if (event.code === 'Digit4' || event.code === 'Numpad4') {
      reRecordIndex = 3;
    } else if (event.code === 'Digit5' || event.code === 'Numpad5') {
      reRecordIndex = 4;
    } else if (event.key === 'Esc' || event.key === 'Escape') {
      if (this.isRecording) {
        trackRecording('discard-ongoing', this.props.locale);
        await this.discardRecording();
      }
    }

    if (reRecordIndex !== null) {
      trackRecording('rerecord', this.props.locale);
      await this.discardRecording();
      this.setState({
        rerecordIndex: reRecordIndex,
      });
    }
  };

  private getRecordingIndex() {
    return (
      this.state.rerecordIndex ??
      this.state.clips.findIndex(({ recording }) => !recording)
    );
  }

  private releaseMicrophone = () => {
    if (!document.hidden) {
      return;
    }

    if (this.isRecording) {
      this.saveRecording();
    }
    this.audio.release();
  };

  private processRecording = (info: AudioInfo, recordingIndex: number) => {
    const error = this.getRecordingError(recordingIndex);
    if (error) {
      return this.setState({ error });
    }

    this.setState(({ clips }) => {
      const newClips = [...clips];
      newClips[recordingIndex].recording = Array.isArray(
        newClips[recordingIndex].recording
      )
        ? [...newClips[recordingIndex].recording, info]
        : [info];
      return {
        clips: newClips,
        rerecordIndex: null,
        showBackdrop: false,
      };
    });

    trackRecording('record', this.props.locale);
  };

  private getRecordingError = (idx?: number): RecordingError => {
    const length = this.recordingStopTime - this.recordingStartTime;
    const currentSentence =
      this.state.clips[idx != undefined ? idx : this.getRecordingIndex()]
        .sentence;
    const minClipLength = currentSentence.taxonomy
      ? MIN_RECORDING_MS_BENCHMARK
      : MIN_RECORDING_MS;

    if (length < minClipLength) {
      return RecordingError.TOO_SHORT;
    }
    if (length > MAX_RECORDING_MS) {
      return RecordingError.TOO_LONG;
    }
    // If updateVolume was never called, we assume there’s a problem with the
    // AnalyserNode and skip this error.
    if (this.maxVolume !== -1 && this.maxVolume < MIN_VOLUME) {
      return RecordingError.TOO_QUIET;
    }
    return null;
  };

  private updateVolume = (volume: number) => {
    if (volume > this.maxVolume) {
      this.maxVolume = volume;
    }
  };

  private rerecord = async (i: number) => {
    trackRecording('rerecord', this.props.locale);
    await this.discardRecording();
    const newClips = [...this.state.clips];
    newClips[i].recording = [];

    this.setState({
      rerecordIndex: i,
      clips: newClips,
    });
  };

  private onClipCkeck(sentenceIdx: number, clipIdx: number) {
    const newClips = [...this.state.clips];
    newClips[sentenceIdx].recording[clipIdx].checked = true;
    this.setState({
      clips: newClips,
    });
  }

  private handleRecordClick = async () => {
    if (this.state.recordingStatus === 'waiting') return;
    const isRecording = this.isRecording;

    this.setState({ recordingStatus: 'waiting' });

    if (isRecording) {
      this.saveRecording();
      if (this.stopScheudle) {
        clearTimeout(this.stopScheudle);
        this.stopScheudle = undefined;
      }
      return;
    }

    try {
      await this.audio.init();
      await this.startRecording();
    } catch (err) {
      if (err in AudioError) {
        this.setState({ error: err });
      } else {
        throw err;
      }
    }
  };

  private startRecording = async () => {
    this.showWave = true;
    if (this.state.useExternalMic) {
      console.log('Recording with external mic...');

      this.setState({ recordProgress: 0, recordedClips: [] });
      this.recordingStartTime = Date.now();
      this.recordingStopTime = 0;
      const currentIndex = this.getRecordingIndex();
      this.props.api
        .startRecording(
          this.state.devices,
          this.state.clips[currentIndex].sentence.audioLength
        )
        .then(clipNames => {
          this.setState({
            audioNames: clipNames.audio_files,
          });
          this.handleRecordClick();
        });

      // this.stopScheudle = this.scheudleStop(
      //   this.state.clips[currentIndex].sentence.audioLength || 5
      // );

      // Stop waves when audo length ends
      if (this.state.clips[currentIndex].sentence.audioLength) {
        setTimeout(() => {
          this.showWave = false;
          this.setState({ showBackdrop: true });
        }, (this.state.clips[currentIndex].sentence.audioLength + 3) * 1000);
      }

      this.setState({
        recordingCountdown: 2,
      });

      const countdown = setInterval(() => {
        const currentCountdown = this.state.recordingCountdown;
        if (currentCountdown == 0) {
          this.setState({
            recordingStatus: 'recording',
            recordingCountdown: null,
          });
          this.startProgresTimer(currentIndex);
          clearInterval(countdown);
        } else {
          this.setState({
            recordingCountdown: currentCountdown - 1,
          });
        }
      }, 1000);

      this.setState({
        recordingStatus: 'waiting',
        error: null,
      });
    } else {
      console.log('Recording with integrated mic...');
      try {
        await this.audio.start();
        this.setState({ recordProgress: 0 });
        this.maxVolume = -1; // Initialize to -1 in case updateVolume is never called.
        this.recordingStartTime = Date.now();
        this.recordingStopTime = 0;
        const currentIndex = this.getRecordingIndex();

        if (this.state.clips[currentIndex].sentence.audioLength) {
          this.setState({ recordProgress: 0 });
          this.stopScheudle = this.scheudleStop(
            this.state.clips[currentIndex].sentence.audioLength
          );
          this.timer = setInterval(() => {
            this.setState({
              recordProgress:
                (Date.now() - this.recordingStartTime) /
                (this.state.clips[currentIndex].sentence.audioLength * 1000),
            });
          }, 1000);
        }

        this.setState({
          recordingStatus: 'recording',
          error: null,
        });
      } catch (err) {
        this.setState({
          recordingStatus: null,
        });
      }
    }
  };

  private startProgresTimer = (currentIndex: number) => {
    if (this.state.clips[currentIndex].sentence.audioLength) {
      this.setState({ recordProgress: 0 });

      this.timer = setInterval(() => {
        this.setState({
          recordProgress:
            (Date.now() - this.recordingStartTime) /
            (this.state.clips[currentIndex].sentence.audioLength * 1000),
        });
      }, 1000);
    }
  };

  private scheudleStop = (time: number): NodeJS.Timeout => {
    return setTimeout(() => {
      this.handleRecordClick();
    }, time * 1000);
  };

  private getDevMac = (fileName: string): { dev_mac: string; file: string } => {
    return {
      dev_mac: fileName.split('.')[0].split('_').pop(),
      file: fileName,
    };
  };

  private saveRecording = () => {
    // We noticed that some people hit the Stop button too early, cutting off
    // the recording prematurely. To compensate, we add a short buffer to the
    // end of each recording (issue #1648).
    const RECORD_STOP_DELAY = 500;
    setTimeout(async () => {
      const recordingIndex = this.getRecordingIndex();
      if (this.state.useExternalMic) {
        const idx = recordingIndex;
        if (this.timer) clearInterval(this.timer);

        for (let i = 0; i < this.state.audioNames.length; i++) {
          const audioName = this.state.audioNames[i];
          const audioData = await this.props.api.getRecord(audioName.file);
          const amplifiedAudioData = await this.props.api.getRecord(
            `${audioName.file}.amplified`
          );
          this.processRecording(
            {
              blob: audioData,
              url: amplifiedAudioData
                ? URL.createObjectURL(amplifiedAudioData)
                : URL.createObjectURL(audioData),
              microphoneId: audioName.dev_mac,
            },
            idx
          );
        }
        // Just 1
        // const audioName = this.state.audioNames[0];
        // const audioData = await this.props.api.getRecord(audioName);
        // this.processRecording({
        //   blob: audioData,
        //   url: URL.createObjectURL(audioData),
        // });
      } else {
        const info = await this.audio.stop();
        if (this.timer) clearInterval(this.timer);
        this.processRecording(info, recordingIndex);
      }
    }, RECORD_STOP_DELAY);
    this.recordingStopTime = Date.now();
    this.setState({
      recordingStatus: null,
      recordProgress: 0,
    });
  };

  private discardRecording = async () => {
    if (!this.isRecording) return;
    await this.audio.stop();
    this.setState({ recordingStatus: null });
  };

  private cancelReRecord = async () => {
    await this.discardRecording();
    this.setState({ rerecordIndex: null });
  };

  private handleSkip = async () => {
    const { api, removeSentences } = this.props;
    await this.discardRecording();
    const current = this.getRecordingIndex();
    const id = this.state.clips[current]?.sentence?.id;
    // api.skipSentence(id);
    removeSentences([id]);
    this.setState(({ clips }) => {
      const newClips = [...clips];
      newClips[current] = { recording: null, sentence: null };
      // if (clips[current]?.recording?.url)
      //   URL.revokeObjectURL(clips[current].recording.url);
      return {
        clips: newClips,
        error: null,
      };
    });
  };

  private handleDevice = (deviceType: string, devices?: string[]) => {
    this.setState({
      useExternalMic: deviceType == 'external',
      showSelectDevice: false,
      devices: devices,
    });
  };

  private fetchDevices = async () => {
    const devices = await this.props.api.fetchDevices();
    if (devices?.devices?.length > 0) {
      return this.setState({
        devices: devices.devices,
        showSelectDevice: true,
        loading: false,
      });
    }
  };

  private upload = (hasAgreed: boolean = false) => {
    const {
      // addAchievement,
      addNotification,
      addUploads,
      api,
      locale,
      removeSentences,
      tallyRecording,
      user,
      refreshUser,
    } = this.props;

    const newClips = [...this.state.clips];

    addUploads([
      ...newClips.map(({ sentence, recording }) => async () => {
        let success = false;

        while (success === false && recording) {
          try {
            for (let i = 0; i < recording.length; i++) {
              const r = recording[i];

              if (!r.submited) {
                await api.uploadClip(
                  r.blob,
                  sentence.id,
                  r.microphoneId,
                  this.props.location.state.recordingLang.toUpperCase(),
                  this.demoMode
                );

                URL.revokeObjectURL(r.url);

                r.submited = true;
              }
            }

            if (!user.account) {
              tallyRecording();
            }
            success = true;
          } catch (error) {
            const msg = 'Upload of this clip failed. Retry?';
            await new Promise(resolve => setTimeout(resolve, 1000));
            if (!confirm(msg)) {
              success = true;
            }
          }
        }
      }),
      async () => {
        trackRecording('submit', locale);
        // refreshUser();
        addNotification(
          <>
            <CheckIcon />{' '}
            <Localized id="clips-uploaded">
              <span />
            </Localized>
          </>
        );
      },
    ]);

    this.setState({
      clips: newClips,
    });

    return true;
  };

  private resetState = (callback?: any) =>
    this.setState(initialState, callback);

  private displayError = () => {
    return (
      this.isUnsupportedPlatform ||
      (!this.props.isLoading && this.state.clips.length == 0)
    );
  };

  private returnSpeakError = () => {
    return this.isUnsupportedPlatform ? (
      <UnsupportedInfo />
    ) : (
      <NoSentencesAvailable isLoading={this.state.loading} />
    );
  };

  private resetAndGoHome = () => {
    const { history, toLocaleRoute } = this.props;
    this.resetState(() => {
      history.push(toLocaleRoute(URLS.ROOT));
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    });
  };

  private getExtendedClips(clips: SentenceRecording[]): SentenceRecording[] {
    let extendedClips: SentenceRecording[] = [];
    clips.forEach(c => {
      if (c.sentence.repsCount) {
        const array: SentenceRecording[] = new Array(c.sentence.repsCount)
          .fill(null)
          .map(() => ({ ...c }));
        extendedClips.push(...array);
      } else {
        extendedClips.push(c);
      }
    });
    if (this.props.location.state.recordingLang) {
      extendedClips = extendedClips.filter(
        i => i.sentence.locale == this.props.location.state.recordingLang
      );
    }

    return extendedClips;
  }

  private submiteableEelements = () => {
    const { clips } = this.state;
    const result = clips.filter(
      clip =>
        clip.recording?.length > 0 &&
        clip.recording.some(r => r.blob && !r.submited)
    );
    return result;
  };

  private canSubmit = () => {
    let result: boolean;
    const submiteable = this.submiteableEelements();

    if (ENVIRONMENT.must_check) {
      result =
        submiteable.length > 0 &&
        submiteable.every(clip => clip.recording.every(rec => rec.checked));
    } else {
      result = submiteable.length > 0;
    }

    return result;
  };

  private mustReview = () => {
    return this.submiteableEelements().length > 0;
  };

  render() {
    const { getString, user } = this.props;
    // let { clips } = this.state;
    const {
      clips,
      isSubmitted,
      error,
      recordingStatus,
      rerecordIndex,
      recordProgress,
      showSelectDevice,
      recordedClips,
      recordingCountdown,
      devices,
    } = this.state;
    const recordingIndex = this.getRecordingIndex();

    return (
      <>
        <div id="speak-page">
          {!isSubmitted && (
            <NavigationPrompt
              when={
                clips.filter(clip => clip.recording).length > 0 &&
                this.canSubmit()
              }>
              {({ onConfirm, onCancel }: any) => (
                <Modal innerClassName="record-abort" onRequestClose={onCancel}>
                  <Localized id="record-abort-title">
                    <h1 className="title" />
                  </Localized>
                  <Localized id="record-abort-text">
                    <p className="text" />
                  </Localized>
                  <ModalButtons>
                    <Localized id="record-abort-submit">
                      <Button
                        outline
                        rounded
                        className={getTrackClass('fs', 'exit-submit-clips')}
                        onClick={() => {
                          if (this.upload()) onConfirm();
                        }}
                      />
                    </Localized>
                    <Localized id="record-abort-continue">
                      <Button
                        outline
                        rounded
                        className={getTrackClass(
                          'fs',
                          'exit-continue-recording'
                        )}
                        onClick={onCancel}
                      />
                    </Localized>
                  </ModalButtons>
                  <Localized id="record-abort-delete">
                    <TextButton
                      className={getTrackClass('fs', 'exit-delete-clips')}
                      onClick={onConfirm}
                    />
                  </Localized>
                </Modal>
              )}
            </NavigationPrompt>
          )}

          {showSelectDevice ? (
            <div className="select-device-wrapper">
              <SelectDevice
                onSelectDevice={this.handleDevice}
                devices={devices}
              />
            </div>
          ) : (
            <ContributionPage
              showBackdrop={this.state.showBackdrop}
              demoMode={this.demoMode}
              canSubmit={this.canSubmit}
              mustReview={this.mustReview}
              activeIndex={recordingIndex}
              errorContent={this.displayError() && this.returnSpeakError()}
              recordProgress={recordProgress}
              instruction={(props: any) =>
                error ? (
                  <div className="error">
                    <Localized
                      id={
                        {
                          [RecordingError.TOO_SHORT]: 'record-error-too-short',
                          [RecordingError.TOO_LONG]: 'record-error-too-long',
                          [RecordingError.TOO_QUIET]: 'record-error-too-quiet',
                          [AudioError.NOT_ALLOWED]:
                            'record-must-allow-microphone',
                          [AudioError.NO_MIC]: 'record-no-mic-found',
                          [AudioError.NO_SUPPORT]:
                            'record-platform-not-supported',
                        }[error]
                      }
                      {...props}
                    />
                  </div>
                ) : (
                  <Localized
                    id={
                      this.isRecording
                        ? 'record-stop-instruction'
                        : recordingIndex === SET_COUNT - 1
                        ? 'record-last-instruction'
                        : ['record-instruction', 'record-again-instruction'][
                            recordingIndex
                          ] || 'record-again-instruction2'
                    }
                    elems={{
                      recordIcon: <MicIcon />,
                      stopIcon: <StopIcon />,
                    }}
                    {...props}
                  />
                )
              }
              isFirstSubmit={user.recordTally === 0}
              isPlaying={this.showWave && this.isRecording}
              isSubmitted={isSubmitted}
              onReset={() => this.resetState()}
              onSkip={this.handleSkip}
              onSubmit={() => this.upload()}
              recordingStatus={recordingStatus}
              primaryButtons={
                <RecordButton
                  trackClass="speak-record"
                  countdown={recordingCountdown}
                  status={recordingStatus}
                  onClick={this.handleRecordClick}
                />
              }
              pills={clips.map((clip, i) => (props: ContributionPillProps) => (
                <RecordingPill
                  {...props}
                  clip={clip}
                  onCheck={idx => this.onClipCkeck(i, idx)}
                  status={
                    recordingIndex === i
                      ? 'active'
                      : clip.recording
                      ? 'done'
                      : 'pending'
                  }
                  onRerecord={() => this.rerecord(i)}>
                  {rerecordIndex === i && (
                    <Localized id="record-cancel">
                      <TextButton
                        onClick={this.cancelReRecord}
                        className="text"
                      />
                    </Localized>
                  )}
                </RecordingPill>
              ))}
              reportModalProps={{
                reasons: [
                  'offensive-language',
                  'grammar-or-spelling',
                  'different-language',
                  'difficult-pronounce',
                ],
                kind: 'sentence',
                id:
                  recordingIndex == -1 || !clips[recordingIndex].sentence
                    ? null
                    : clips[recordingIndex].sentence.id,
              }}
              sentences={clips.map(({ sentence }) => sentence)}
              shortcuts={[
                {
                  key: 'shortcut-record-toggle',
                  label: 'shortcut-record-toggle-label',
                  action: this.handleRecordClick,
                },
                {
                  key: 'shortcut-rerecord-toggle',
                  label: 'shortcut-rerecord-toggle-label',
                  action: this.handleRecordClick,
                },
                {
                  key: 'shortcut-discard-ongoing-recording',
                  label: 'shortcut-discard-ongoing-recording-label',
                  // This is handled in handleKeyUp, separately.
                  action: () => {},
                },
                {
                  key: 'shortcut-submit',
                  label: 'shortcut-submit-label',
                  icon: <ReturnKeyIcon />,
                  // This is handled in handleKeyUp, separately.
                  action: () => {},
                },
              ]}
              type="speak"
            />
          )}
        </div>
      </>
    );
  }
}

const mapStateToProps = (state: StateTree) => {
  const { sentences, isLoading } = Sentences.selectors.localeSentences(state);

  return {
    api: state.api,
    locale: state.locale,
    sentences,
    user: state.user,
    isLoading,
  };
};

const mapDispatchToProps = {
  addNotification: Notifications.actions.addPill,
  addAchievement: Notifications.actions.addAchievement,
  addUploads: Uploads.actions.add,
  removeSentences: Sentences.actions.remove,
  tallyRecording: User.actions.tallyRecording,
  refreshUser: User.actions.refresh,
  updateUser: User.actions.update,
};

export default withRouter(
  localeConnector(
    withLocalization(
      connect<PropsFromState, any>(
        mapStateToProps,
        mapDispatchToProps
      )(SpeakPage)
    )
  )
);
